import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateEPF, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const EPFCalculator = () => {
  const navigate = useNavigate();
  const [epfBasic, setEpfBasic] = useState(25000);
  const [epfAge, setEpfAge] = useState(25);
  const [epfRate] = useState(8.15);
  const [epfIncrease, setEpfIncrease] = useState(5);

  const epfResult = useMemo(
    () => calculateEPF(epfBasic, epfAge, epfRate, epfIncrease),
    [epfBasic, epfAge, epfRate, epfIncrease]
  );

  const epfPieData = [
    { name: "Employee", value: epfResult.employee, color: "#e2e8f0" },
    { name: "Employer", value: epfResult.employer, color: "#94a3b8" },
    { name: "Interest", value: epfResult.interest, color: "#f59e0b" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                EPF Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-blue-100 p-3">
                      <Wallet className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        EPF Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Employee Provident Fund
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Basic Salary + DA"
                      value={epfBasic}
                      onChange={setEpfBasic}
                      min={1000}
                      max={500000}
                      step={500}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Current Age"
                      value={epfAge}
                      onChange={setEpfAge}
                      min={18}
                      max={58}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-blue-600"
                    />

                    <CompactCalculatorRow
                      label="Annual Increase (%)"
                      value={epfIncrease}
                      onChange={setEpfIncrease}
                      min={0}
                      max={20}
                      step={1}
                      suffix="%"
                      colorClass="text-purple-600"
                    />

                    <CompactCalculatorRow
                      label="EPF Interest Rate"
                      value={epfRate}
                      readOnly
                      suffix="%"
                      colorClass="text-blue-600"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Accumulated Corpus
                    </p>
                    <h2 className="text-4xl font-black text-blue-700">
                      {formatCurrency(epfResult.maturity)}
                    </h2>
                  </div>
                  <div className="mb-6 flex h-[200px] w-full justify-center">
                    <ResponsiveContainer width={200} height="100%">
                      <PieChart>
                        <Pie
                          data={epfPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          dataKey="value"
                          stroke="none"
                        >
                          {epfPieData.map(e => (
                            <Cell key={e.name} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v: number | undefined) =>
                            v !== undefined ? formatCurrency(v) : ""
                          }
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 py-2">
                      <span className="font-medium text-muted-foreground">
                        Employee Share
                      </span>
                      <span className="text-lg font-bold text-secondary">
                        {formatCurrency(epfResult.employee)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-100 py-2">
                      <span className="font-medium text-muted-foreground">
                        Employer Share
                      </span>
                      <span className="text-lg font-bold text-secondary">
                        {formatCurrency(epfResult.employer)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-100 py-2">
                      <span className="font-medium text-muted-foreground">
                        Total Interest
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        {formatCurrency(epfResult.interest)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
